import React, { SetStateAction, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Flex,
	IconButton,
	Image,
	Link as ChakraLink,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Spinner,
	Text,
} from '@chakra-ui/react';

import { MoreIcon } from 'shared/icons';
import { getFilenameFromURL } from 'shared/utils/get-filename-from-url';

import { AssetInfo } from '../../../../../widgets/hero-update-modal';
import { DragAndDropWrapper } from '../drag-and-drop-wrapper';
import { useAssetCardLayerByIdQuery } from './queries.gen';

interface AssetCardLayerProps {
	assets: string[];
	assetId: string;
	// openDetailsModal: () => void;
	setCurrentAssetInfo?: React.Dispatch<SetStateAction<AssetInfo>>;
	handleSetFieldValue: (
		field: string,
		value: any,
		shouldValidate?: any,
	) => void;
	isActive?: boolean;
	handlePhotoDownload: (imageLink: string, imageName: any) => void;
}

export const AssetCardLayer: React.FC<AssetCardLayerProps> = ({
	assets,
	assetId,
	// openDetailsModal,
	setCurrentAssetInfo,
	handleSetFieldValue,
	isActive,
	handlePhotoDownload,
}) => {
	const { data, loading } = useAssetCardLayerByIdQuery({
		variables: {
			id: assetId,
		},
	});

	// const handleOpenAssetDetails = () => {
	// 	setCurrentAssetInfo(assetId);
	// 	openDetailsModal();
	// };

	const handleDeleteAsset = useCallback(() => {
		handleSetFieldValue(
			'layers',
			assets.filter((id) => id !== assetId),
		);
		if (setCurrentAssetInfo) {
			setCurrentAssetInfo((prev) => ({
				...prev,
				layers: prev.layers.includes(assetId)
					? prev.layers.filter((layerId) => layerId !== assetId)
					: prev.layers,
			}));
		}
	}, [assetId, assets, handleSetFieldValue, setCurrentAssetInfo]);

	const imageName = getFilenameFromURL(data?.layer.link || '');
	const handleDownloadAsset = useCallback(() => {
		if (data) {
			handlePhotoDownload(data.layer.link || '', imageName);
		}
	}, [data, handlePhotoDownload, imageName]);

	const handleAddLayers = (prevLayers: string[]) =>
		prevLayers.length < 5 ? [...prevLayers, assetId] : [...prevLayers];

	return (
		<DragAndDropWrapper assetId={assetId}>
			<Flex flexDirection="column" gap={2} w="240px" maxW="240px">
				<Box
					h="min-content"
					userSelect="none"
					border="4px solid transparent"
					borderRadius="10px"
					position="relative"
				>
					<Flex
						height="300px"
						alignItems="center"
						justifyContent="center"
						border="1px solid #EEEEF2"
						borderRadius="10px"
						overflow="hidden"
						borderColor={isActive ? '#644DED' : ''}
						_hover={{
							borderColor: '#644DED',
							cursor: 'pointer',
						}}
					>
						{loading ? (
							<Spinner size="md" />
						) : (
							<Box
								onClick={() =>
									setCurrentAssetInfo
										? setCurrentAssetInfo((prev) => ({
												...prev,
												layers: prev.layers.includes(assetId)
													? prev.layers.filter((layerId) => layerId !== assetId)
													: handleAddLayers(prev.layers),
										  }))
										: null
								}
							>
								<Image
									alt={data?.layer.name || ''}
									src={data?.layer.link || ''}
									h="300px"
									objectFit="contain"
								/>
							</Box>
						)}
					</Flex>
					{!loading && (
						<Box position="absolute" top={1} right={1}>
							<Menu placement="bottom-start">
								<MenuButton
									as={IconButton}
									aria-label="Options"
									icon={<MoreIcon w={5} h={5} />}
									variant="ghost"
								/>
								<MenuList rootProps={{ transform: 'scale(0)' }}>
									<ChakraLink
										as={Link}
										to={`/art-catalog/?id=${assetId}&name=${data?.layer.name}&link=${data?.layer.link}&type=layer`}
										target="_blank"
										_hover={{ textDecoration: 'none' }}
										_focus={{ outline: 'none' }}
									>
										<MenuItem>Open in Art Catalog</MenuItem>
									</ChakraLink>
									<MenuItem onClick={handleDeleteAsset}>Unlink Asset</MenuItem>
									<MenuItem onClick={handleDownloadAsset}>
										Download Asset
									</MenuItem>
								</MenuList>
							</Menu>
						</Box>
					)}
				</Box>
				<Text p={1} fontSize="12px" fontWeight={600} lineHeight="16px">
					{data?.layer.name || ''}
				</Text>
			</Flex>
		</DragAndDropWrapper>
	);
};
